import React, { forwardRef } from "react"
import cx from "classnames"

import {
  ButtonTemplatePropsType,
  getButtonContent,
  getButtonCommonClassNames,
} from "../helpers"
import Button, { ButtonProps, LinkProps } from "../ButtonComponent"

type GhostCommonPropsType = ButtonTemplatePropsType<{
  standalone?: boolean
  destructive?: boolean
}>

export type GhostButtonPropsType = GhostCommonPropsType & ButtonProps
export type GhostLinkPropsType = GhostCommonPropsType & LinkProps

const getComputedClassName = ({
  className,
  standalone,
  destructive,
}: GhostCommonPropsType) =>
  cx(className, "cob-Button__ghost", {
    "cob-Button__ghost--standalone": standalone,
    "cob-Button__ghost--destructive": destructive,
  })

const _GhostButton = forwardRef<HTMLButtonElement, GhostButtonPropsType>(
  (props: GhostButtonPropsType, ref) => {
    const {
      children,
      standalone,
      destructive,
      icon,
      iconPosition,
      className,
      loading,
      disabled,
      rounded,
      size,
      fullWidth,
      ...restButtonProps
    } = props
    const content = getButtonContent({ children, icon, iconPosition })
    const isDisabled = disabled || loading
    const buttonClassNames = getButtonCommonClassNames({
      disabled: isDisabled,
      rounded,
      size,
      fullWidth,
      className,
    })

    return (
      <Button
        {...restButtonProps}
        className={getComputedClassName({
          className: buttonClassNames,
          standalone,
          destructive,
        })}
        ref={ref}
        loading={loading}
        disabled={isDisabled}
      >
        {content}
      </Button>
    )
  }
)
_GhostButton.displayName = "GhostButton"

const GhostLink = forwardRef<HTMLAnchorElement, GhostLinkPropsType>(
  (props: GhostLinkPropsType, ref) => {
    const {
      children,
      standalone,
      destructive,
      icon,
      iconPosition,
      className,
      loading,
      disabled,
      rounded,
      size,
      fullWidth,
      ...restLinkProps
    } = props
    const content = getButtonContent({ children, icon, iconPosition })
    const isDisabled = disabled || loading
    const buttonClassNames = getButtonCommonClassNames({
      disabled: isDisabled,
      rounded,
      size,
      fullWidth,
      className,
    })

    return (
      <Button.Link
        {...restLinkProps}
        className={getComputedClassName({
          className: buttonClassNames,
          standalone,
          destructive,
        })}
        ref={ref}
        loading={loading}
        disabled={isDisabled}
      >
        {content}
      </Button.Link>
    )
  }
)
GhostLink.displayName = "GhostLink"

const GhostButton = Object.assign(_GhostButton, { Link: GhostLink })

export default GhostButton
